/**
 * THE PENDING-CONSUMER LIST — the impact verdict's third channel, both surfaces.
 *
 * A SIBLING of BreakingConsumerList, deliberately not a mode of it. `breaking` refuses the change
 * and `pending` does not: `impact_of_change` puts a consumer here when the change would leave that
 * consumer referencing data nobody has committed yet, which is a sequencing fact for the producer
 * rather than a reason to stop them. Folding the two into one component with a severity flag would
 * put the refusal and the non-refusal one boolean apart, on the two controls that decide whether an
 * operator can publish.
 *
 * THE ROW'S REAL SHAPE, which is NOT `breaking`'s. `impact_of_change` emits pending rows as
 * `dict(row, pending=after_pending)` — `{kind, name, path, pending}` with NO `lines`, the same way
 * it emits `warnings` (`validate_data_refs.py:1846-1849`). Reading `lines` here would print
 * "(no line located)" on every row and read as a rendering fault rather than as the checker having
 * nothing to say about position.
 */

import type { DataImpactRow } from '../api/client'

export interface PendingConsumerListProps {
  /** The verdict's `pending` rows. Absent and empty both render nothing. */
  rows?: DataImpactRow[]
  className?: string
}

export function PendingConsumerList({ rows, className }: PendingConsumerListProps) {
  if (!rows || rows.length === 0) return null

  return (
    <div className={className ?? 'text-xs'} data-testid="pending-data-consumers">
      <p className="text-amber-800">
        {rows.length} declared consumer{rows.length === 1 ? '' : 's'} reference data that is not
        committed yet. This does not block the change. Where the publish gate runs, it is refused
        until each row lands:
      </p>
      <ul className="list-disc list-inside text-amber-800">
        {rows.map((r, i) => (
          <li key={`${r.kind}:${r.name}:${r.path}:${i}`}>
            {r.kind} <strong>{r.name}</strong> — <code className="font-mono">{r.path}</code>
            {r.pending && r.pending.length > 0 && ` — ${r.pending.join('; ')}`}
          </li>
        ))}
      </ul>
    </div>
  )
}
